import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { t } from "@/shared/lib/th-translations";
import type { MachineContribution } from "@/shared/types/production";

const THB_FORMATTER = new Intl.NumberFormat("th-TH", {
  style: "currency",
  currency: "THB",
  maximumFractionDigits: 0,
});

function formatTHB(n: number): string {
  return THB_FORMATTER.format(n);
}

function formatTime(ts: number): string {
  const d = new Date(ts + 7 * 3600_000);
  return d.toISOString().slice(11, 16);
}

interface Props {
  open: boolean;
  onClose: () => void;
  machine: string;
  /** When null = total across window. Otherwise YYYY-MM-DD filter. */
  day: string | null;
  rows: MachineContribution[];
}

interface TaskGroup {
  taskNumber: string;
  processTitle: string;
  stageTotalQty: number;
  invoiceAmount: number;
  subtotalQty: number;
  subtotalShots: number;
  subtotalValue: number;
  subtotalWaste: number;
  targetQuantity: number;
  operatorCount: number;
  items: MachineContribution[];
}

export default function MachineContributionsModal({
  open,
  onClose,
  machine,
  day,
  rows,
}: Props) {
  const filtered = useMemo(
    () => (day === null ? rows : rows.filter((r) => r.day === day)),
    [rows, day],
  );

  const groups = useMemo<TaskGroup[]>(() => {
    const map = new Map<string, MachineContribution[]>();
    for (const r of filtered) {
      const arr = map.get(r.taskNumber);
      if (arr) arr.push(r);
      else map.set(r.taskNumber, [r]);
    }
    return Array.from(map.entries())
      .map(([taskNumber, items]): TaskGroup => {
        const first = items[0];
        const subtotalQty = items.reduce((s, r) => s + r.qty, 0);
        const subtotalShots = items.reduce((s, r) => s + r.shots, 0);
        const subtotalValue = items.reduce((s, r) => s + r.value, 0);
        const subtotalWaste = items.reduce((s, r) => s + r.wasteQty, 0);
        const operatorCount = new Set(items.map((r) => r.employeeId)).size;
        return {
          taskNumber,
          processTitle: first.processTitle,
          stageTotalQty: first.stageTotalQty,
          invoiceAmount: first.invoiceAmount,
          subtotalQty,
          subtotalShots,
          subtotalValue,
          subtotalWaste,
          targetQuantity: first.targetQuantity,
          operatorCount,
          items: [...items].sort((a, b) => b.qty - a.qty),
        };
      })
      .sort((a, b) => b.subtotalValue - a.subtotalValue);
  }, [filtered]);

  const totalQty = filtered.reduce((s, r) => s + r.qty, 0);
  const totalShots = filtered.reduce((s, r) => s + r.shots, 0);
  const totalValue = filtered.reduce((s, r) => s + r.value, 0);
  const totalWaste = filtered.reduce((s, r) => s + r.wasteQty, 0);

  const title =
    day === null
      ? t("production.machines.drill.titleTotal", { machine })
      : t("production.machines.drill.titleDay", { day, machine });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[min(95vw,1400px)] w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {groups.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">
            {t("production.machines.drill.empty")}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 px-1 pb-3 text-xs">
              <SummaryCell
                label={t("production.machines.drill.summary.totalTasks")}
                value={groups.length.toLocaleString()}
              />
              <SummaryCell
                label={t("production.machines.drill.summary.rows")}
                value={filtered.length.toLocaleString()}
              />
              <SummaryCell
                label={t("production.machines.drill.summary.totalQty")}
                value={totalQty.toLocaleString()}
              />
              <SummaryCell
                label={t("production.machines.drill.summary.totalWaste")}
                value={totalWaste.toLocaleString()}
              />
              <SummaryCell
                label={t("production.machines.drill.summary.totalShots")}
                value={totalShots.toLocaleString()}
              />
              <SummaryCell
                label={t("production.machines.drill.summary.totalValue")}
                value={formatTHB(totalValue)}
              />
            </div>

            <div className="flex-1 overflow-auto space-y-4 pr-1">
              {groups.map((g) => (
                <TaskGroupCard key={g.taskNumber} group={g} />
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TaskGroupCard({ group }: { group: TaskGroup }) {
  const shareSum = group.subtotalQty > 0
    ? group.items.reduce((s, r) => s + (r.qty / group.subtotalQty) * 100, 0)
    : 0;
  return (
    <div className="border rounded-md overflow-hidden">
      {/* Group header */}
      <div className="bg-blue-50/60 border-b px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-1">
        <Link
          to={`/production/log?task=${encodeURIComponent(group.taskNumber)}`}
          className="text-blue-700 hover:underline font-mono text-sm font-semibold"
        >
          {group.taskNumber}
        </Link>
        <span className="text-sm text-muted-foreground">{group.processTitle}</span>
        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-medium">
          {t("production.machines.drill.group.operators", { n: group.operatorCount })}
        </span>
      </div>

      {/* Group body */}
      <table className="w-full text-sm border-collapse">
        <thead className="bg-gray-50">
          <tr className="border-b text-xs text-muted-foreground">
            <th className="px-3 py-2 text-left font-medium whitespace-nowrap">
              {t("production.machines.drill.col.operator")}
            </th>
            <th className="px-3 py-2 text-center font-medium whitespace-nowrap">
              {t("production.machines.drill.col.time")}
            </th>
            <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
              {t("production.machines.drill.col.targetQty")}
            </th>
            <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
              {t("production.machines.drill.col.qty")}
            </th>
            <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
              {t("production.machines.drill.col.waste")}
            </th>
            <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
              {t("production.machines.drill.col.overproduction")}
            </th>
            <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
              {t("production.machines.drill.col.shots")}
            </th>
            <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
              {t("production.machines.drill.group.shareInTask")}
            </th>
            <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
              {t("production.machines.drill.col.value")}
            </th>
          </tr>
        </thead>
        <tbody>
          {group.items.map((r, idx) => {
            const shareTask =
              group.subtotalQty > 0 ? (r.qty / group.subtotalQty) * 100 : 0;
            const totalOver = group.stageTotalQty > group.targetQuantity && group.targetQuantity > 0
              ? group.stageTotalQty - group.targetQuantity : 0;
            const operatorOver = totalOver > 0 && group.stageTotalQty > 0
              ? Math.round((r.qty / group.stageTotalQty) * totalOver) : 0;
            return (
              <tr
                key={`${r.taskNumber}-${r.timestamp}-${r.employeeId}-${idx}`}
                className="border-b last:border-b-0 hover:bg-gray-50/60"
              >
                <td className="px-3 py-2 whitespace-nowrap">
                  <Link
                    to={`/production/people/${encodeURIComponent(r.employeeId)}`}
                    className="text-blue-600 hover:underline"
                  >
                    {r.employeeName}
                  </Link>
                </td>
                <td className="px-3 py-2 text-center whitespace-nowrap font-mono text-xs text-muted-foreground">
                  {r.day} {formatTime(r.timestamp)}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums text-muted-foreground">
                  -
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                  {r.qty.toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums text-red-500">
                  {r.wasteQty > 0 ? r.wasteQty.toLocaleString() : '-'}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums text-amber-600">
                  {operatorOver > 0 ? `+${operatorOver.toLocaleString()}` : '-'}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums text-muted-foreground">
                  {r.shots > 0 ? r.shots.toLocaleString() : '-'}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums font-medium">
                  {shareTask.toFixed(1)}%
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums font-medium">
                  {formatTHB(r.value)}
                </td>
              </tr>
            );
          })}
          {/* Subtotal row */}
          <tr className="bg-gray-50 border-t font-medium">
            <td
              colSpan={2}
              className="px-3 py-2 text-right text-xs text-muted-foreground"
            >
              {t("production.machines.drill.group.subtotal")}
            </td>
            <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
              {group.targetQuantity.toLocaleString()}
            </td>
            <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
              {group.subtotalQty.toLocaleString()}
            </td>
            <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums text-red-500">
              {group.subtotalWaste > 0 ? group.subtotalWaste.toLocaleString() : '-'}
            </td>
            <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums text-amber-600">
              {group.stageTotalQty > group.targetQuantity && group.targetQuantity > 0
                ? `+${(group.stageTotalQty - group.targetQuantity).toLocaleString()}`
                : '-'}
            </td>
            <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
              {group.subtotalShots.toLocaleString()}
            </td>
            <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
              {shareSum.toFixed(1)}%
            </td>
            <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
              {formatTHB(group.subtotalValue)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function SummaryCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-gray-50/60 px-3 py-2">
      <div className="text-muted-foreground">{label}</div>
      <div className="font-semibold tabular-nums">{value}</div>
    </div>
  );
}
